import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useState } from 'react';
import { Authenticated, usePermissions } from 'react-admin';
import { BillingExportStats as BillingExportStatsType } from '../../interfaces/billing.async';
import { IClinic } from '../../interfaces/clinic';
import { getClinicsForSelect } from '../../lib/clinic';
import { AuthService } from '../../services';
import { BillingService } from '../../services/billing';
import { Roles } from '../../util/constants';
import { SectionTitle } from '../common/SectionTitle';

// Allowed clinic IDs from environment
const ALLOWED_CLINIC_IDS: number[] = JSON.parse(
  process.env.REACT_APP_DRCHRONO_ALLOWED_CLINIC_IDS || '[]',
);

export const BillingExportStats: FC = () => {
  const { loaded } = usePermissions();
  const [stats, setStats] = useState<BillingExportStatsType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clinicId, setClinicId] = useState<number>(0);
  const [clinics, setClinics] = useState<IClinic[]>([]);
  const [clinicsLoading, setClinicsLoading] = useState(true);

  const fetchStats = async () => {
    if (!clinicId) return;

    setIsLoading(true);
    setError(null);

    try {
      const billingService = new BillingService();
      const response = await billingService.getBillingExportStats(clinicId);

      if (response) {
        setStats(response.stats);
      } else {
        setError('Failed to load statistics');
      }
    } catch (err) {
      setError('Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  };

  // Load clinics on mount
  useEffect(() => {
    const loadClinics = async () => {
      setClinicsLoading(true);
      try {
        const clinicsData = await getClinicsForSelect({ ids: ALLOWED_CLINIC_IDS });
        setClinics(clinicsData);
        // Set default clinic to first one
        if (clinicsData.length > 0) {
          setClinicId(clinicsData[0].id);
        }
      } catch (err) {
        console.error('Failed to load clinics:', err);
      } finally {
        setClinicsLoading(false);
      }
    };
    loadClinics();
  }, []);

  useEffect(() => {
    if (clinicId) {
      fetchStats();
    }
  }, [clinicId]);

  if (!loaded) {
    return <Typography>Loading...</Typography>;
  }

  if (!AuthService.checkRoleAccess(Roles.Billing)) {
    return (
      <Card>
        <CardContent>
          <Container maxWidth='md'>
            <Grid container spacing={4} justifyContent='center' alignItems='center'>
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center' }}>
                  <LockIcon color='error' sx={{ fontSize: 48 }} />
                  <Typography>You do not have access to this page</Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </CardContent>
      </Card>
    );
  }

  const formatDuration = (ms?: number): string => {
    if (!ms) return '-';
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Authenticated>
      <Card>
        <CardContent>
          <SectionTitle label='Export Statistics' variant='h5' style={{ fontWeight: '500' }} />
          <Divider sx={{ mb: 2 }} />

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl size='small' sx={{ minWidth: 250 }}>
              <InputLabel>Clinic</InputLabel>
              <Select
                value={clinicId}
                label='Clinic'
                onChange={(e) => setClinicId(Number(e.target.value))}
                disabled={clinicsLoading}>
                {clinicsLoading ? (
                  <MenuItem value={0}>Loading...</MenuItem>
                ) : (
                  clinics.map((clinic) => (
                    <MenuItem key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <IconButton onClick={fetchStats} disabled={isLoading || clinicsLoading}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Error */}
          {error && (
            <Typography color='error' sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Loading */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Stats */}
          {stats && !isLoading && (
            <>
              {/* Total */}
              <Paper variant='outlined' sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                <Typography variant='h2' fontWeight={600} color='primary'>
                  {stats.total}
                </Typography>
                <Typography variant='h6' color='text.secondary'>
                  Total Exports
                </Typography>
              </Paper>

              {/* By Status */}
              <Typography variant='h6' sx={{ mb: 2 }}>
                By Status
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={2.4}>
                  <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
                    <HourglassEmptyIcon sx={{ fontSize: 32, color: 'grey.500', mb: 1 }} />
                    <Typography variant='h5' fontWeight={600}>
                      {stats.byStatus.pending}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Pending
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
                    <SyncIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                    <Typography variant='h5' fontWeight={600} color='primary.main'>
                      {stats.byStatus.processing}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Processing
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                    <Typography variant='h5' fontWeight={600} color='success.main'>
                      {stats.byStatus.completed}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Completed
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
                    <WarningIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                    <Typography variant='h5' fontWeight={600} color='warning.main'>
                      {stats.byStatus.completedWithErrors}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      With Errors
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
                    <ErrorIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
                    <Typography variant='h5' fontWeight={600} color='error.main'>
                      {stats.byStatus.failed}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Failed
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Recent Activity */}
              <Typography variant='h6' sx={{ mb: 2 }}>
                Recent Activity
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant='h4' fontWeight={600}>
                      {stats.recentActivity.today}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Today
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant='h4' fontWeight={600}>
                      {stats.recentActivity.last7Days}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Last 7 Days
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant='h4' fontWeight={600}>
                      {stats.recentActivity.last30Days}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Last 30 Days
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Average Processing Time */}
              {stats.averageProcessingTimeMs !== undefined && (
                <>
                  <Typography variant='h6' sx={{ mb: 2 }}>
                    Performance
                  </Typography>
                  <Paper variant='outlined' sx={{ p: 2 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Average Processing Time
                    </Typography>
                    <Typography variant='h5' fontWeight={600}>
                      {formatDuration(stats.averageProcessingTimeMs)}
                    </Typography>
                  </Paper>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Authenticated>
  );
};

export default BillingExportStats;
